import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { partnerAPI, profileAPI, sessionAPI, locationAPI, notificationAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { 
  Users, Calendar as CalendarIcon, Clock, MapPin, Activity, Heart, MessageCircle, 
  User, Settings, ArrowLeft, Send, Check, X, Dumbbell, Trophy, Bell, Navigation,
  CalendarPlus, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const TIME_SLOTS = [
  { value: 'morning', label: 'Morning (6am-12pm)', icon: '🌅' },
  { value: 'afternoon', label: 'Afternoon (12pm-5pm)', icon: '☀️' },
  { value: 'evening', label: 'Evening (5pm-9pm)', icon: '🌆' },
  { value: 'night', label: 'Night (9pm+)', icon: '🌙' }
];

export const FindPartner = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('find');
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [connections, setConnections] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profile, setProfile] = useState(null);
  
  // Location state
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [maxDistance, setMaxDistance] = useState(25);
  
  // Availability state
  const [availability, setAvailability] = useState([]);
  const [lookingForPartner, setLookingForPartner] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  
  // Request dialog
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [requestForm, setRequestForm] = useState({ sport: '', day: '', time_slot: '', message: '' });
  
  // Session dialog
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [sessionForm, setSessionForm] = useState({ sport: '', date: null, time_slot: '', location: '', notes: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, partnersRes, requestsRes, connectionsRes, sessionsRes, notifRes] = await Promise.all([
        profileAPI.get(),
        partnerAPI.findPartners(),
        partnerAPI.getRequests(),
        partnerAPI.getConnections(),
        sessionAPI.getAll(),
        notificationAPI.getAll()
      ]);
      
      setProfile(profileRes.data);
      setAvailability(profileRes.data.availability || []);
      setLookingForPartner(profileRes.data.looking_for_partner || false);
      setLocationEnabled(!!profileRes.data.coordinates);
      setPartners(partnersRes.data.partners || []);
      setRequests(requestsRes.data);
      setConnections(connectionsRes.data.connections || []);
      setSessions(sessionsRes.data.sessions || []);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(notifRes.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const enableLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    setGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await locationAPI.updateLocation(position.coords.latitude, position.coords.longitude);
          setLocationEnabled(true);
          toast.success('Location enabled! Finding nearby partners...');
          
          // Reload partners with location
          const nearbyRes = await partnerAPI.findNearbyPartners(maxDistance);
          setPartners(nearbyRes.data.partners || []);
        } catch (error) {
          toast.error('Failed to save location');
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        toast.error('Unable to get your location');
        setGettingLocation(false);
      }
    );
  };

  const findNearbyPartners = async () => {
    try {
      const res = await partnerAPI.findNearbyPartners(maxDistance);
      setPartners(res.data.partners || []);
    } catch (error) {
      toast.error('Failed to find nearby partners');
    }
  };

  const toggleAvailability = (day, timeSlot) => {
    const exists = availability.some(a => a.day === day && a.time_slot === timeSlot);
    if (exists) {
      setAvailability(availability.filter(a => !(a.day === day && a.time_slot === timeSlot)));
    } else {
      setAvailability([...availability, { day, time_slot: timeSlot }]);
    }
  };

  const isAvailable = (day, timeSlot) => {
    return availability.some(a => a.day === day && a.time_slot === timeSlot);
  };

  const saveAvailability = async () => {
    setSavingAvailability(true);
    try {
      await partnerAPI.updateAvailability({ 
        availability, 
        looking_for_partner: lookingForPartner 
      });
      toast.success('Availability updated!');
      const partnersRes = locationEnabled 
        ? await partnerAPI.findNearbyPartners(maxDistance)
        : await partnerAPI.findPartners();
      setPartners(partnersRes.data.partners || []);
    } catch (error) {
      toast.error('Failed to save availability');
    } finally {
      setSavingAvailability(false);
    }
  };

  const openRequestDialog = (partner) => {
    setSelectedPartner(partner);
    setRequestForm({
      sport: partner.common_sports?.[0] || '',
      day: partner.availability_matches?.[0]?.day || '',
      time_slot: partner.availability_matches?.[0]?.time_slot || '',
      message: ''
    });
    setShowRequestDialog(true);
  };

  const sendPartnerRequest = async () => {
    if (!selectedPartner) return;
    
    try {
      await partnerAPI.sendRequest(selectedPartner.user_id, requestForm);
      toast.success('Partner request sent!');
      setShowRequestDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const handleRequestResponse = async (requestId, action) => {
    try {
      await partnerAPI.respondToRequest(requestId, action);
      toast.success(action === 'accept' ? 'Partner request accepted!' : 'Request declined');
      loadData();
    } catch (error) {
      toast.error('Failed to respond to request');
    }
  };

  const openSessionDialog = (connection) => {
    setSelectedConnection(connection);
    setSessionForm({
      sport: connection.sport || '',
      date: null,
      time_slot: '',
      location: '',
      notes: ''
    });
    setShowSessionDialog(true);
  };

  const createSession = async () => {
    if (!selectedConnection || !sessionForm.date || !sessionForm.time_slot) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await sessionAPI.create({
        connection_id: selectedConnection.connection_id,
        sport: sessionForm.sport,
        date: format(sessionForm.date, 'yyyy-MM-dd'),
        time_slot: sessionForm.time_slot,
        location: sessionForm.location,
        notes: sessionForm.notes
      });
      toast.success('Workout session scheduled!');
      setShowSessionDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create session');
    }
  };

  const cancelSession = async (sessionId) => {
    try {
      await sessionAPI.cancel(sessionId);
      toast.success('Session cancelled');
      loadData();
    } catch (error) {
      toast.error('Failed to cancel session');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await notificationAPI.markRead(notificationId);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Failed to mark notification read');
    }
  };

  const levelColors = {
    beginner: 'bg-green-500',
    intermediate: 'bg-blue-500',
    advanced: 'bg-yellow-500',
    elite: 'bg-orange-500',
    college_pro: 'bg-red-500'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/discover')} className="hover:bg-gray-100">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Button>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <h1 className="font-display font-bold text-xl uppercase text-gray-900">Find a Partner</h1>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative hover:bg-gray-100"
            onClick={() => setActiveTab('notifications')}
          >
            <Bell className="w-5 h-5 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full text-xs flex items-center justify-center text-white">
                {unreadCount}
              </span>
            )}
          </Button>
        </div>
      </header>

      <main className="pt-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-5 bg-gray-100">
              <TabsTrigger value="find" className="font-body text-xs data-[state=active]:bg-primary data-[state=active]:text-white">
                <Dumbbell className="w-4 h-4 mr-1" /> Find
              </TabsTrigger>
              <TabsTrigger value="schedule" className="font-body text-xs data-[state=active]:bg-primary data-[state=active]:text-white">
                <CalendarIcon className="w-4 h-4 mr-1" /> Schedule
              </TabsTrigger>
              <TabsTrigger value="requests" className="font-body text-xs data-[state=active]:bg-primary data-[state=active]:text-white relative">
                <Send className="w-4 h-4 mr-1" /> Requests
                {requests.incoming.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full text-[10px] flex items-center justify-center text-white">
                    {requests.incoming.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="sessions" className="font-body text-xs data-[state=active]:bg-primary data-[state=active]:text-white">
                <CalendarPlus className="w-4 h-4 mr-1" /> Sessions
              </TabsTrigger>
              <TabsTrigger value="partners" className="font-body text-xs data-[state=active]:bg-primary data-[state=active]:text-white">
                <Trophy className="w-4 h-4 mr-1" /> Partners
              </TabsTrigger>
            </TabsList>

            {/* Find Partners Tab */}
            <TabsContent value="find" className="mt-6">
              {/* Location Toggle */}
              <Card className="bg-white border-gray-100 shadow-sm mb-4">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${locationEnabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        <Navigation className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-body font-medium text-gray-900">Location-Based Search</p>
                        <p className="font-body text-sm text-gray-500">
                          Find partners near you
                        </p>
                      </div>
                    </div>
                    {!locationEnabled ? (
                      <Button 
                        size="sm" 
                        onClick={enableLocation}
                        disabled={gettingLocation}
                      >
                        {gettingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable'}
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{maxDistance} mi</span>
                        <Input
                          type="range"
                          min={5}
                          max={50}
                          value={maxDistance}
                          onChange={(e) => setMaxDistance(parseInt(e.target.value))}
                          onMouseUp={findNearbyPartners}
                          className="w-24"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {!lookingForPartner ? (
                <Card className="bg-white border-gray-100 shadow-sm">
                  <CardContent className="pt-6 text-center">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-display text-xl uppercase mb-2 text-gray-900">Enable Partner Mode</h3>
                    <p className="font-body text-gray-500 mb-6">
                      Set your availability to find workout partners who match your schedule
                    </p>
                    <Button onClick={() => setActiveTab('schedule')} className="bg-primary">
                      Set Your Schedule
                    </Button>
                  </CardContent>
                </Card>
              ) : partners.length === 0 ? (
                <Card className="bg-white border-gray-100 shadow-sm">
                  <CardContent className="pt-6 text-center">
                    <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                    <h3 className="font-display text-xl uppercase mb-2">No Partners Found</h3>
                    <p className="font-body text-gray-500">
                      {locationEnabled 
                        ? `No partners within ${maxDistance} miles. Try increasing the distance.`
                        : 'Enable location to find partners nearby!'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {partners.map((partner) => (
                    <Card 
                      key={partner.user_id}
                      data-testid={`partner-card-${partner.user_id}`}
                      className="bg-white border-gray-100 shadow-sm overflow-hidden"
                    >
                      <div className="flex">
                        <div className="w-32 h-40">
                          <img
                            src={partner.photos?.[0] || 'https://via.placeholder.com/150'}
                            alt={partner.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-display font-bold text-lg">{partner.name}, {partner.age}</h3>
                              <div className="flex items-center gap-2">
                                {partner.distance && (
                                  <p className="font-body text-xs text-accent flex items-center gap-1">
                                    <Navigation className="w-3 h-3" /> {partner.distance} mi
                                  </p>
                                )}
                                {partner.location && (
                                  <p className="font-body text-xs text-gray-500 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {partner.location}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge className="bg-primary/20 text-primary border-0">
                              {partner.match_score}%
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-1 mb-3">
                            {partner.common_sports?.map((sport, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {sport}
                              </Badge>
                            ))}
                          </div>
                          
                          {partner.availability_matches?.length > 0 && (
                            <div className="mb-3">
                              <p className="font-body text-xs text-gray-500 mb-1">Available:</p>
                              <div className="flex flex-wrap gap-1">
                                {partner.availability_matches.slice(0, 3).map((slot, i) => (
                                  <span key={i} className="text-xs px-2 py-1 rounded bg-accent/20 text-accent capitalize">
                                    {slot.day.slice(0,3)} {slot.time_slot}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <Button 
                            data-testid={`request-partner-${partner.user_id}`}
                            size="sm" 
                            className="w-full bg-primary hover:bg-primary/90"
                            onClick={() => openRequestDialog(partner)}
                          >
                            <Send className="w-4 h-4 mr-1" /> Request
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Schedule Tab */}
            <TabsContent value="schedule" className="mt-6">
              <Card className="bg-white border-gray-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-primary" />
                    Your Availability
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${lookingForPartner ? 'bg-primary' : 'bg-muted'}`}>
                        <Users className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-body font-medium">Looking for Workout Partners</p>
                        <p className="font-body text-sm text-gray-500">
                          Allow others to find you
                        </p>
                      </div>
                    </div>
                    <Checkbox
                      data-testid="looking-for-partner-toggle"
                      checked={lookingForPartner}
                      onCheckedChange={setLookingForPartner}
                    />
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr>
                          <th className="p-2 text-left font-body text-sm text-gray-500">Day</th>
                          {TIME_SLOTS.map((slot) => (
                            <th key={slot.value} className="p-2 text-center font-body text-xs text-gray-500">
                              <span className="block text-lg">{slot.icon}</span>
                              {slot.label.split(' ')[0]}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {DAYS.map((day) => (
                          <tr key={day}>
                            <td className="p-2 font-body text-sm capitalize">{day}</td>
                            {TIME_SLOTS.map((slot) => (
                              <td key={slot.value} className="p-2 text-center">
                                <button
                                  data-testid={`availability-${day}-${slot.value}`}
                                  className={`w-10 h-10 rounded-lg transition-colors ${
                                    isAvailable(day, slot.value)
                                      ? 'bg-primary text-white'
                                      : 'bg-secondary hover:bg-secondary/80'
                                  }`}
                                  onClick={() => toggleAvailability(day, slot.value)}
                                >
                                  {isAvailable(day, slot.value) && <Check className="w-5 h-5 mx-auto" />}
                                </button>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <Button 
                    data-testid="save-availability-btn"
                    className="w-full bg-primary hover:bg-primary/90"
                    onClick={saveAvailability}
                    disabled={savingAvailability}
                  >
                    {savingAvailability ? 'Saving...' : 'Save Availability'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests" className="mt-6 space-y-6">
              <Card className="bg-white border-gray-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-display text-lg uppercase">Incoming Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  {requests.incoming.length === 0 ? (
                    <p className="font-body text-gray-500 text-center py-4">No pending requests</p>
                  ) : (
                    <div className="space-y-4">
                      {requests.incoming.map((req) => (
                        <div key={req.request_id} className="flex items-center gap-4 p-4 rounded-lg bg-secondary">
                          <div className="w-12 h-12 rounded-full overflow-hidden">
                            <img
                              src={req.from_user?.photos?.[0] || 'https://via.placeholder.com/100'}
                              alt={req.from_user?.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="font-body font-medium">{req.from_user?.name}</p>
                            <p className="font-body text-sm text-gray-500">
                              {req.sport} • {req.proposed_day} {req.proposed_time}
                            </p>
                            {req.message && (
                              <p className="font-body text-sm mt-1">"{req.message}"</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                              onClick={() => handleRequestResponse(req.request_id, 'decline')}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => handleRequestResponse(req.request_id, 'accept')}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-display text-lg uppercase">Sent Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  {requests.outgoing.length === 0 ? (
                    <p className="font-body text-gray-500 text-center py-4">No sent requests</p>
                  ) : (
                    <div className="space-y-4">
                      {requests.outgoing.map((req) => (
                        <div key={req.request_id} className="flex items-center gap-4 p-4 rounded-lg bg-secondary">
                          <div className="w-12 h-12 rounded-full overflow-hidden">
                            <img
                              src={req.to_user?.photos?.[0] || 'https://via.placeholder.com/100'}
                              alt={req.to_user?.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="font-body font-medium">{req.to_user?.name}</p>
                            <p className="font-body text-sm text-gray-500">
                              {req.sport} • {req.proposed_day} {req.proposed_time}
                            </p>
                          </div>
                          <Badge className={
                            req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                            req.status === 'accepted' ? 'bg-green-500/20 text-green-500' :
                            'bg-red-500/20 text-red-500'
                          }>
                            {req.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Sessions Tab */}
            <TabsContent value="sessions" className="mt-6">
              <Card className="bg-white border-gray-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
                    <CalendarPlus className="w-5 h-5 text-primary" />
                    Scheduled Workouts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sessions.length === 0 ? (
                    <div className="text-center py-8">
                      <CalendarPlus className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                      <p className="font-body text-gray-500">No scheduled sessions</p>
                      <p className="font-body text-sm text-gray-500">
                        Schedule a workout with your partners!
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sessions.filter(s => s.status !== 'cancelled').map((session) => (
                        <div 
                          key={session.session_id}
                          className="p-4 rounded-lg bg-secondary"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full overflow-hidden">
                                <img
                                  src={session.partner?.photo || 'https://via.placeholder.com/100'}
                                  alt={session.partner?.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <p className="font-body font-medium">{session.partner?.name}</p>
                                <Badge variant="secondary">{session.sport}</Badge>
                              </div>
                            </div>
                            <Badge className={
                              session.status === 'scheduled' ? 'bg-blue-500/20 text-blue-500' :
                              session.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                              'bg-red-500/20 text-red-500'
                            }>
                              {session.status}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                            <div className="flex items-center gap-2 text-gray-500">
                              <CalendarIcon className="w-4 h-4" />
                              {session.date}
                            </div>
                            <div className="flex items-center gap-2 text-gray-500">
                              <Clock className="w-4 h-4" />
                              {TIME_SLOTS.find(t => t.value === session.time_slot)?.label.split(' ')[0]}
                            </div>
                            {session.location && (
                              <div className="flex items-center gap-2 text-gray-500 col-span-2">
                                <MapPin className="w-4 h-4" />
                                {session.location}
                              </div>
                            )}
                          </div>
                          
                          {session.notes && (
                            <p className="font-body text-sm text-gray-500 mb-3">
                              {session.notes}
                            </p>
                          )}
                          
                          {session.status === 'scheduled' && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                onClick={() => cancelSession(session.session_id)}
                              >
                                Cancel
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => navigate('/messages')}
                              >
                                <MessageCircle className="w-4 h-4 mr-1" /> Message
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Partners Tab */}
            <TabsContent value="partners" className="mt-6">
              <Card className="bg-white border-gray-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-primary" />
                    Your Workout Partners
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {connections.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                      <p className="font-body text-gray-500">No workout partners yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {connections.map((conn) => (
                        <div 
                          key={conn.connection_id}
                          className="flex items-center gap-4 p-4 rounded-lg bg-secondary"
                        >
                          <div className="w-14 h-14 rounded-full overflow-hidden">
                            <img
                              src={conn.photos?.[0] || 'https://via.placeholder.com/100'}
                              alt={conn.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="font-display font-bold">{conn.name}</p>
                            {conn.sport && (
                              <Badge variant="secondary" className="mt-1">{conn.sport}</Badge>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openSessionDialog(conn)}
                            >
                              <CalendarPlus className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => navigate('/messages')}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="mt-6">
              <Card className="bg-white border-gray-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
                    <Bell className="w-5 h-5 text-primary" />
                    Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {notifications.length === 0 ? (
                    <div className="text-center py-8">
                      <Bell className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                      <p className="font-body text-gray-500">No notifications</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {notifications.map((notif) => (
                        <div 
                          key={notif.notification_id}
                          className={`p-4 rounded-lg cursor-pointer transition-colors ${
                            notif.read ? 'bg-secondary' : 'bg-primary/10 border border-primary/20'
                          }`}
                          onClick={() => !notif.read && markNotificationRead(notif.notification_id)}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-body font-medium">{notif.title}</p>
                              <p className="font-body text-sm text-gray-500">{notif.message}</p>
                            </div>
                            {!notif.read && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                          <p className="font-body text-xs text-gray-500 mt-2">
                            {new Date(notif.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
        <div className="max-w-lg mx-auto px-4 py-2 flex items-center justify-around">
          <Button variant="ghost" className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary" onClick={() => navigate('/discover')}>
            <Activity className="w-5 h-5" />
            <span className="text-xs font-body">Discover</span>
          </Button>
          <Button variant="ghost" className="flex flex-col items-center gap-1 text-primary">
            <Users className="w-5 h-5" />
            <span className="text-xs font-body">Partners</span>
          </Button>
          <Button variant="ghost" className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary" onClick={() => navigate('/matches')}>
            <Heart className="w-5 h-5" />
            <span className="text-xs font-body">Matches</span>
          </Button>
          <Button variant="ghost" className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary" onClick={() => navigate('/messages')}>
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs font-body">Messages</span>
          </Button>
          <Button variant="ghost" className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary" onClick={() => navigate('/settings')}>
            <Settings className="w-5 h-5" />
            <span className="text-xs font-body">Settings</span>
          </Button>
        </div>
      </nav>

      {/* Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="bg-white border-gray-100 shadow-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase">
              Request {selectedPartner?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-body">Sport</Label>
              <Select value={requestForm.sport} onValueChange={(v) => setRequestForm({ ...requestForm, sport: v })}>
                <SelectTrigger className="bg-background border-gray-100 shadow-sm">
                  <SelectValue placeholder="Select sport" />
                </SelectTrigger>
                <SelectContent>
                  {selectedPartner?.common_sports?.map((sport) => (
                    <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body">Day</Label>
                <Select value={requestForm.day} onValueChange={(v) => setRequestForm({ ...requestForm, day: v })}>
                  <SelectTrigger className="bg-background border-gray-100 shadow-sm">
                    <SelectValue placeholder="Day" />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((day) => (
                      <SelectItem key={day} value={day} className="capitalize">{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-body">Time</Label>
                <Select value={requestForm.time_slot} onValueChange={(v) => setRequestForm({ ...requestForm, time_slot: v })}>
                  <SelectTrigger className="bg-background border-gray-100 shadow-sm">
                    <SelectValue placeholder="Time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((slot) => (
                      <SelectItem key={slot.value} value={slot.value}>{slot.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-body">Message (optional)</Label>
              <Textarea
                placeholder="Hey! Want to workout together?"
                className="bg-background border-gray-100 shadow-sm"
                value={requestForm.message}
                onChange={(e) => setRequestForm({ ...requestForm, message: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={sendPartnerRequest}>
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Dialog */}
      <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
        <DialogContent className="bg-white border-gray-100 shadow-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase">
              Schedule Workout with {selectedConnection?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-body">Sport</Label>
              <Input
                className="bg-background border-gray-100 shadow-sm"
                value={sessionForm.sport}
                onChange={(e) => setSessionForm({ ...sessionForm, sport: e.target.value })}
                placeholder="e.g., Running, Tennis"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-body">Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-background border-gray-100 shadow-sm">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {sessionForm.date ? format(sessionForm.date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-gray-100 shadow-sm">
                  <Calendar
                    mode="single"
                    selected={sessionForm.date}
                    onSelect={(date) => setSessionForm({ ...sessionForm, date })}
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="font-body">Time</Label>
              <Select value={sessionForm.time_slot} onValueChange={(v) => setSessionForm({ ...sessionForm, time_slot: v })}>
                <SelectTrigger className="bg-background border-gray-100 shadow-sm">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((slot) => (
                    <SelectItem key={slot.value} value={slot.value}>{slot.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-body">Location (optional)</Label>
              <Input
                className="bg-background border-gray-100 shadow-sm"
                value={sessionForm.location}
                onChange={(e) => setSessionForm({ ...sessionForm, location: e.target.value })}
                placeholder="e.g., Central Park, Local Gym"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-body">Notes (optional)</Label>
              <Textarea
                className="bg-background border-gray-100 shadow-sm"
                value={sessionForm.notes}
                onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })}
                placeholder="Any details about the workout..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSessionDialog(false)}>Cancel</Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={createSession}>
              Schedule Workout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
